import { Expand, Minimize2, Terminal, X } from "lucide-react";
import { useEffect } from "react";
import { match } from "ts-pattern";
import { ExecuteCommandDisplay } from "@/components/agent/toolDisplay/ExecuteCommandDisplay";
import { GenericToolDisplay } from "@/components/agent/toolDisplay/GenericToolDisplay";
import { cn } from "@/lib/utils";
import { AgentMessage } from "@/models/agent/message";
import { getAgentThreadStore } from "@/stores/agent/agentThreadStore";
import { useAgentStore } from "@/stores/agent/useAgentStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./AgentDialog";
import { Button } from "./Button";

interface ToolPanelProps {
  threadId: string;
  selectedToolCallMessage: AgentMessage | null;
  currentToolResponseMessage: AgentMessage | null;
  className?: string;
}

export const ToolPanel: React.FC<ToolPanelProps> = ({
  threadId,
  selectedToolCallMessage,
  currentToolResponseMessage,
  className = "",
}) => {
  const isToolPanelExpanded = useAgentStore((state) => state.isToolPanelExpanded);
  const setIsToolPanelExpanded = useAgentStore((state) => state.setIsToolPanelExpanded);
  const isOpen = getAgentThreadStore(threadId)((state) => state.isOpenToolPanel);
  const setIsOpenToolPanel = getAgentThreadStore(threadId)((state) => state.setIsOpenToolPanel);

  // Escキーでパネルを閉じる
  useEffect(() => {
    if (isToolPanelExpanded) return; // DialogがEscキーを処理するので無効化

    // 右サイドパネル用のEscキー処理
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpenToolPanel?.(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setIsOpenToolPanel, isToolPanelExpanded]);

  // ツールコンテンツの共通部分
  const toolContent = selectedToolCallMessage?.toolCall
    ? match(selectedToolCallMessage.toolCall?.toolName).with("execute_command", () => (
      <ExecuteCommandDisplay
        toolExecution={{
          toolName: selectedToolCallMessage.toolCall?.toolName || "execute_command",
          command: selectedToolCallMessage.toolCall?.parameters.command as string || "",
          output: currentToolResponseMessage?.toolResult?.result?.output as string | undefined,
          executedAt: selectedToolCallMessage.createdAt,
        }}
      />
    )).otherwise(() => (
      <GenericToolDisplay
        toolCallMessage={selectedToolCallMessage}
        toolResponseMessage={currentToolResponseMessage}
      />
    ))
    : (
      /* No Tool Execution State */
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <Terminal className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No tools executed yet</p>
          <p className="text-xs text-gray-400 mt-1">Tool usage will appear here when available</p>
        </div>
      </div>
    );

  // モーダル表示
  if (isOpen && isToolPanelExpanded) {
    return (
      <Dialog
        open={isToolPanelExpanded}
        onOpenChange={(open) => {
          setIsOpenToolPanel?.(open);
        }}
      >
        <DialogContent className="max-w-[1280px] max-h-[calc(100vh-40px)] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0 border-b border-gray-200">
            <div className="flex items-center justify-between py-4 pr-12 pl-4">
              <DialogTitle className="font-medium text-md">Tool use</DialogTitle>
              <Button
                onClick={() => setIsToolPanelExpanded(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-gray-500"
                title="Minimize to side panel"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto mt-[-16px] min-h-40">
            {toolContent}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 通常の右サイドパネル表示
  return (
    <div
      className={cn(
        "my-5 overflow-hidden transition-all duration-150 ease-in-out",
        isOpen && !isToolPanelExpanded
          ? "max-[1440px]:w-[420px] max-[1680px]:w-[560px] w-[640px] max-w-[640px] max-h-full opacity-100"
          : "max-w-0 max-h-0 opacity-0",
        className,
      )}
    >
      <div className="flex flex-col h-full mr-5 bg-white rounded-2xl border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 gap-2">
          <h3 className="flex-1 text-sm font-medium text-gray-900">Tool use</h3>
          <Button
            onClick={() => setIsToolPanelExpanded(true)}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-gray-500"
            title="Expand to modal"
          >
            <Expand className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => setIsOpenToolPanel?.(false)}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-gray-500"
            title="Close"
          >
            <X className="h-4 w-4 scale-125" />
          </Button>
        </div>

        {toolContent}
      </div>
    </div>
  );
};
